import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarDismiss } from '@angular/material/snack-bar';
import { SrpPlatformService } from '@salesrun/ngx-platform';
import { Observable } from 'rxjs';
import { DialogButtonState } from './DialogButtonState';
import { InputDialogResult, SrpInputDialogComponent } from './input-dialog';
import { SrpQuestionDialogComponent } from './question-dialog/srp-question-dialog.component';


@Injectable()
export class DialogService {
    constructor(
        private _matDialog: MatDialog,
        private _snackbar: MatSnackBar,
        private _platformService: SrpPlatformService,
    ) {
    }

    error(message: string): Observable<string> {
        return this.ok(message, 'Error');
    }

    success(message: string): Observable<string> {
        return this.ok(message, 'Success');
    }

    completed(message: string, title = 'Completed'): Observable<string> {
        return this.ok(message, title);
    }

    snackbar(message: string, action?: string, config?: MatSnackBarConfig): Observable<MatSnackBarDismiss> {
        config = {
            duration: 4000,
            ...(config ?? {}),
        };
        action = action ?? 'OK';
        const snackbar = this._snackbar.open(message, action, config);
        return snackbar.afterDismissed();
    }

    selectItem(message?: string, action?: string): Observable<MatSnackBarDismiss> {
        message = message || 'Please, select item';
        return this.snackbar(message, action);
    }

    ok(message: string, title?: string, config?: MatDialogConfig, styles?: NgStyleValue): Observable<string> {
        return this.question(message, ['OK'], title, config, styles);
    }

    question(
        message: string,
        choices: DialogButtonState[],
        title?: string,
        config?: MatDialogConfig,
        styles?: NgStyleValue,
        mainStyles?: NgStyleValue,
        closeVisible: boolean = false,
    ): Observable<string> {
        const questionDialogConfig: MatDialogConfig = {
            ...config,
            autoFocus: false,
            disableClose: true,
        };
        const dialogReference: MatDialogRef<SrpQuestionDialogComponent> = this._matDialog.open(SrpQuestionDialogComponent, questionDialogConfig);
        dialogReference.componentInstance.title = title;
        dialogReference.componentInstance.message = message;
        dialogReference.componentInstance.choices = choices;
        dialogReference.componentInstance.closeVisible = closeVisible;
        dialogReference.componentInstance.styles = styles;
        dialogReference.componentInstance.mainStyles = mainStyles
            ? mainStyles
            : {
                'max-width': '700px',
            };
        return dialogReference.afterClosed();
    }

    confirm(message: string, choices: DialogButtonState[], config?: MatDialogConfig, styles?: NgStyleValue): Observable<string> {
        return this.question(message, choices, 'Confirmation', config, styles);
    }

    input(
        value: string,
        title: string,
        valueTitle: string,
        maxLength: number,
        validators: ValidatorFn[],
        submitButtonTitle = 'OK',
        multiline = false,
        choices?: DialogButtonState[],
        occupiedNames?: string[],
        message?: string,
        requiredErrorMessage?: string,
        inputAutoFocus: boolean = false,
        submitOnEnter: boolean = false,
        centerButtonsAlign: boolean = false,
    ): Observable<InputDialogResult> {
        const dialogRef = this._matDialog.open<SrpInputDialogComponent, unknown, InputDialogResult>(SrpInputDialogComponent, {
            width: '600px',
            disableClose: true,
            autoFocus: inputAutoFocus,
        });
        dialogRef.componentInstance.original = value;
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.valueTitle = valueTitle;
        dialogRef.componentInstance.validators = validators;
        dialogRef.componentInstance.maxLength = maxLength;
        dialogRef.componentInstance.submitButton = submitButtonTitle;
        dialogRef.componentInstance.multiline = multiline;
        dialogRef.componentInstance.matHintVisible = maxLength != undefined;
        dialogRef.componentInstance.choices = choices;
        dialogRef.componentInstance.message = message;
        dialogRef.componentInstance.requiredErrorMessage = requiredErrorMessage;
        dialogRef.componentInstance.submitOnEnter = submitOnEnter;
        dialogRef.componentInstance.centerButtonsAlign = centerButtonsAlign;

        if (occupiedNames && occupiedNames.length > 0) {
            dialogRef.componentInstance.occupiedNames = occupiedNames;
        }

        return dialogRef.afterClosed();
    }


    openWindow(url: string): boolean {
        if (this._platformService.isTouchScreen) {
            window.location.assign(url);
            return true;
        }

        const newWind = window.open(url, '_blank');
        if (!newWind) {
            this.snackbar('Please allow pop-ups from SalesRun.');
            return false;
        } else {
            newWind.focus();
            return true;
        }
    }
}
